import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileText, Trophy } from "lucide-react";
import { supabase } from "./supabaseClient";
import { buildRayBassLeaderboard, defaultHuntYear, huntDateRange } from "./rayBassHunt";
import PageMeta from "./PageMeta";
import RayBassLeaderboard from "./RayBassLeaderboard";

export default function RayBassHuntPage() {
  const navigate = useNavigate();
  const [year] = useState(() => defaultHuntYear());
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const { start, end } = huntDateRange(year);
    supabase
      .from("catches")
      .select("*")
      .eq("location", "shore")
      .gte("weigh_date", start)
      .lte("weigh_date", end)
      .then(({ data, error: err }) => {
        if (err) throw err;
        setEntries(buildRayBassLeaderboard(data ?? [], year));
      })
      .catch((err) => setError(err.message || "Failed to load catches."))
      .finally(() => setLoading(false));
  }, [year]);

  return (
    <div className="page-shell min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
      <PageMeta
        title="Ray and Bass Hunt | Redruth Sea Angling Association"
        description="Live leaderboard for the RSAA Ray and Bass Hunt — best combined percentage of one ray and one bass caught from the shore during October."
        keywords="RSAA ray and bass hunt, ray fishing Cornwall, bass fishing competition Cornwall, sea angling leaderboard"
        url="https://www.redruthseaanglingassociation.co.uk/ray-bass-hunt"
      />
      <div className="mx-auto max-w-7xl">
        <div className="mb-8 flex items-center gap-4">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="standalone-only group inline-flex items-center gap-1.5 rounded-full border border-cyan-400/20 bg-white/5 px-4 py-2 text-sm font-medium text-slate-200 shadow-lg shadow-black/20 backdrop-blur transition hover:-translate-x-0.5 hover:border-cyan-300/40 hover:bg-cyan-400/10 hover:text-white"
          >
            ← Back
          </button>
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-cyan-400/10 text-cyan-300">
            <Trophy className="h-5 w-5" />
          </div>
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.3em] text-cyan-300">October {year}</p>
            <h1 className="heading-glow text-2xl font-bold text-white sm:text-3xl">Ray and Bass Hunt</h1>
          </div>
        </div>

        <p className="mb-6 text-sm leading-6 text-slate-300">
          Shore only, 8am 1st Oct to 8pm 31st Oct. Best combined CFSA specimen percentage of
          one ray (any species) and one bass, each capped at 150%. Only anglers who have signed in and paid
          entry are shown. Anglers with both a ray and a bass rank above those still waiting on one.
        </p>

        <a
          href="/assets/month_long_shore_comps.pdf"
          target="_blank"
          rel="noopener noreferrer"
          className="mb-8 flex items-center gap-4 rounded-2xl border border-white/10 bg-white/5 p-4 transition hover:border-cyan-300/30 hover:bg-cyan-400/5 panel-lift"
        >
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-cyan-400/10 text-cyan-300">
            <FileText className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-white">Month Long Shore Competition Rules</p>
            <p className="mt-0.5 text-xs text-slate-400">Sign-in, entry fee and weigh-in details</p>
          </div>
          <span className="shrink-0 rounded-lg border border-cyan-300/20 bg-cyan-400/10 px-3 py-1 text-xs font-medium text-cyan-300">
            Open PDF
          </span>
        </a>

        {loading ? (
          <p className="text-cyan-200">Loading leaderboard…</p>
        ) : error ? (
          <p className="text-red-300">Error: {error}</p>
        ) : entries.length === 0 ? (
          <p className="rounded-2xl border border-white/10 bg-white/5 px-4 py-6 text-center text-slate-400">
            No rays or bass logged for the hunt yet.
          </p>
        ) : (
          <RayBassLeaderboard entries={entries} />
        )}
      </div>
    </div>
  );
}
